
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { ProfileDateWheelPicker } from "./ProfileDateWheelPicker";
import { useAdditionalProfiles } from "@/hooks/useAdditionalProfiles";
import { useTranslation } from "react-i18next";

interface CreateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultRelationshipType?: 'gift' | 'memorial' | 'managed';
  onSuccess?: (username: string) => void;
}

export const CreateProfileModal = ({ isOpen, onClose, defaultRelationshipType = 'gift', onSuccess }: CreateProfileModalProps) => {
  const { t } = useTranslation('common');
  const { createProfile, isCreating } = useAdditionalProfiles();
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    birthdate: undefined as Date | undefined,
    relationshipType: defaultRelationshipType
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.firstName) {
      return;
    }

    try {
      await createProfile({
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthdate: formData.birthdate ? format(formData.birthdate, 'yyyy-MM-dd') : undefined,
        relationshipType: formData.relationshipType as 'gift' | 'memorial' | 'managed'
      });
      
      // Reset form and close modal
      const createdUsername = formData.username;
      setFormData({
        username: '',
        firstName: '',
        lastName: '',
        birthdate: undefined,
        relationshipType: defaultRelationshipType
      });
      onClose();
      
      // Navigate to the created profile's timeline
      if (onSuccess) {
        onSuccess(createdUsername);
      }
    } catch (error) {
      // Error is handled by the hook
      console.error('Error in form submission:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      username: '',
      firstName: '',
      lastName: '',
      birthdate: undefined,
      relationshipType: defaultRelationshipType
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {defaultRelationshipType === 'gift' && t('createProfile.giftTitle', 'Create Gift Timeline')}
            {defaultRelationshipType === 'memorial' && t('createProfile.memorialTitle', 'Create Memorial Timeline')}
            {defaultRelationshipType === 'managed' && t('createProfile.managedTitle', 'Create Managed Timeline')}
          </DialogTitle>
          <DialogDescription>
            {t('createProfile.description', 'Create a timeline for someone special in your life.')}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('profile.firstName', 'First Name')} *</Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              placeholder={t('profile.firstNamePlaceholder', 'Enter first name')}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">{t('profile.lastName', 'Last Name')}</Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              placeholder={t('profile.lastNamePlaceholder', 'Enter last name')}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="username">{t('profile.username', 'Username')} *</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value.toLowerCase() }))}
              placeholder={t('profile.usernamePlaceholder', 'Enter username')}
              required
            />
          </div>
          
          <ProfileDateWheelPicker
            id="birthdate"
            label={t('profile.birthdate', 'Birthdate')}
            value={formData.birthdate}
            onChange={(date) => setFormData(prev => ({ ...prev, birthdate: date }))}
          />
          
          <div className="space-y-2">
            <Label>{t('profile.relationshipType', 'Relationship Type')}</Label>
            <Select
              value={formData.relationshipType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, relationshipType: value as any }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gift">{t('profile.giftTimeline', 'Gift Timeline')}</SelectItem>
                <SelectItem value="memorial">{t('profile.memorialTimeline', 'Memorial Timeline')}</SelectItem>
                <SelectItem value="managed">{t('profile.managedTimeline', 'Managed Timeline')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isCreating || !formData.username || !formData.firstName} className="flex-1">
              {isCreating ? t('common.creating', 'Creating...') : t('common.create', 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
